import React, { useMemo, useState, useCallback } from 'react';
import {
    downloadWeeklyReportDocx,
    downloadWeeklyReportPdfWithOverride,
    fetchWeeklyNarrative,
    type WeeklyNarrative,
} from 'src/features/dashboard/api/reports.api';
import WeeklyNarrativeEditModal from './WeeklyNarrativeEditModal';
import { useToast } from 'src/shared/ui/toast/useToast';

function ActionButton({
    label,
    hint,
    onClick,
    disabled,
    loading,
}: {
    label: string;
    hint?: string;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={[
                'rounded-xl border bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 active:scale-[0.99] transition',
                disabled ? 'opacity-60 cursor-not-allowed' : '',
            ].join(' ')}
        >
            <div className="flex items-center gap-2">
                {loading ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                ) : null}
                <div className="font-medium">{label}</div>
            </div>
            {hint ? <div className="mt-0.5 text-[11px] text-gray-500">{hint}</div> : null}
        </button>
    );
}

function normalizeAiText(v: unknown) {
    if (typeof v !== 'string') return '';
    return v
        .replace(/\r?\n+/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

function normalizeUserText(v: unknown) {
    if (typeof v !== 'string') return '';
    return v.replace(/\r\n/g, '\n').trimEnd();
}

function getApiErrorMessage(e: any, fallback: string) {
    return e?.response?.data?.error?.message ?? e?.response?.data?.message ?? e?.message ?? fallback;
}

export default function QuickActions({
    projectId,
    canCreateReport = true,
    reportDisabledReason,
    asOf,
    evBasis = 'CONFIRMED_W1',
}: {
    projectId: string;
    canCreateReport?: boolean;
    reportDisabledReason?: string;
    asOf?: string;
    evBasis?: 'CONFIRMED_W2' | 'CONFIRMED_W1' | 'LIVE';
}) {
    const toast = useToast();

    const [generating, setGenerating] = useState<null | 'pdf' | 'docx'>(null);
    const isBusy = generating != null;

    const [narrativeOpen, setNarrativeOpen] = useState(false);
    const [narrativeLoading, setNarrativeLoading] = useState(false);
    const [narrativeOriginal, setNarrativeOriginal] = useState<WeeklyNarrative | null>(null);
    const [narrativeDraft, setNarrativeDraft] = useState<WeeklyNarrative | null>(null);

    const [narrativeError, setNarrativeError] = useState<string | null>(null);

    const normalizeNarrativeFromAI = useCallback((n: WeeklyNarrative): WeeklyNarrative => {
        const nextActions = (Array.isArray(n.nextActions) ? n.nextActions : [])
            .map((s) => (typeof s === 'string' ? s : ''))
            .slice(0, 30);

        return {
            executiveSummary: normalizeAiText(n.executiveSummary),
            riskComment: normalizeAiText(n.riskComment),
            nextActions,
        };
    }, []);

    const normalizeNarrative = useCallback((n: WeeklyNarrative): WeeklyNarrative => {
        const nextActions = (Array.isArray(n.nextActions) ? n.nextActions : [])
            .map((s) => (typeof s === 'string' ? s : ''))
            .slice(0, 30);

        return {
            executiveSummary: normalizeAiText(n.executiveSummary),
            riskComment: normalizeAiText(n.riskComment),
            nextActions,
        };
    }, []);

const params = useMemo(() => {
    return {
        asOf,
        evBasis,
    } as { asOf?: string; template?: string; evBasis?: 'CONFIRMED_W2' | 'CONFIRMED_W1' | 'LIVE' };
}, [asOf, evBasis]);

    // PDF 형식으로 리포트 생성할 때
    const onPdf = async () => {
        if (isBusy) return;

        if (!canCreateReport) {
            toast.warning(reportDisabledReason ?? '리포트를 생성할 최소한의 정보가 부족합니다.', {
                title: '리포트 생성 불가',
            });
            return;
        }

        const ok = window.confirm('AI로 리포트 초안을 생성하시겠습니까?');
        if (!ok) return;

        setNarrativeOpen(true);
        setNarrativeLoading(true);
        setNarrativeError(null);

        try {
            const result = await fetchWeeklyNarrative(projectId, params);
            const normalized = normalizeNarrativeFromAI(result.narrative);

            setNarrativeOriginal(normalized);
            setNarrativeDraft(normalized);
            setNarrativeError(result.aiWarning ?? null);
        } catch (e: any) {
            toast.error(getApiErrorMessage(e, 'AI 코멘트를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'), {
                title: '불러오기 실패',
            });
            setNarrativeOpen(false);
        } finally {
            setNarrativeLoading(false);
        }
    };

    // 편집창에서 직접 다시 AI 리포트 생성할 때
    const onRegenerateNarrative = async () => {
        if (isBusy) return;

        if (!canCreateReport) {
            toast.warning(reportDisabledReason ?? '리포트를 생성할 최소한의 정보가 부족합니다.', {
                title: '리포트 생성 불가',
            });
            return;
        }

        setNarrativeLoading(true);
        setNarrativeError(null);

        try {
            const result = await fetchWeeklyNarrative(projectId, { ...params, regenerate: true });
            const normalized = normalizeNarrativeFromAI(result.narrative);

            setNarrativeOriginal(normalized);
            setNarrativeDraft(normalized);
            setNarrativeError(result.aiWarning ?? null);
        } catch (e: any) {
            toast.error(getApiErrorMessage(e, 'AI 코멘트를 다시 생성하지 못했습니다.'), {
                title: '재생성 실패',
            });
        } finally {
            setNarrativeLoading(false);
        }
    };

    const onConfirmPdf = async () => {
        if (isBusy) return;

        if (!canCreateReport) {
            toast.warning(reportDisabledReason ?? '리포트를 생성할 최소한의 정보가 부족합니다.', {
                title: '리포트 생성 불가',
            });
            return;
        }

        if (!narrativeDraft) return;

        setGenerating('pdf');

        try {
            const cleaned: WeeklyNarrative = {
                executiveSummary: normalizeUserText(narrativeDraft.executiveSummary),
                riskComment: normalizeUserText(narrativeDraft.riskComment),
                nextActions: (narrativeDraft.nextActions ?? [])
                    .map((s) => (typeof s === 'string' ? s.trim() : ''))
                    .filter(Boolean)
                    .slice(0, 30),
            };

            await downloadWeeklyReportPdfWithOverride(projectId, cleaned, params);

            setNarrativeOpen(false);
            toast.success('PDF 보고서 생성을 완료했습니다.', {
                title: '생성 완료',
            });
        } catch (e: any) {
            toast.error(getApiErrorMessage(e, 'AI를 활용한 보고서 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.'), {
                title: '생성 실패',
            });
        } finally {
            setGenerating(null);
        }
    };

    const onDocx = async () => {
        if (isBusy) return;

        if (!canCreateReport) {
            toast.warning(reportDisabledReason ?? '리포트를 생성할 최소한의 정보가 부족합니다.', {
                title: '리포트 생성 불가',
            });
            return;
        }

        setGenerating('docx');

        try {
            await downloadWeeklyReportDocx(projectId, params);
            toast.success('Word 보고서 생성을 완료했습니다.', {
                title: '생성 완료',
            });
        } catch (e: any) {
            toast.error(getApiErrorMessage(e, 'AI를 활용한 보고서 생성에 실패했습니다. 잠시 후 다시 시도해 주세요.'), {
                title: '생성 실패',
            });
        } finally {
            setGenerating(null);
        }
    };

    return (
        <>
            <div className="flex flex-wrap gap-2">
                <ActionButton
                    label="Create Report"
                    hint={canCreateReport ? 'weekly report in pdf' : (reportDisabledReason ?? 'report unavailable')}
                    onClick={onPdf}
                    disabled={isBusy || !canCreateReport}
                    loading={generating === 'pdf'}
                />
                {/* <ActionButton
                    label="Export Word"
                    hint={
                        canCreateReport
                            ? 'weekly report (.docx)'
                            : reportDisabledReason ?? 'report unavailable'
                    }
                    onClick={onDocx}
                    disabled={isBusy || !canCreateReport}
                    loading={generating === 'docx'}
                /> */}
            </div>

            {isBusy ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div className="rounded-2xl bg-white px-5 py-4 shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" />
                            <div className="text-sm font-medium">
                                {generating === 'pdf' ? 'PDF 보고서를 생성하는 중…' : 'Word 보고서를 생성하는 중…'}
                            </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                            데이터가 많으면 수 초~수십 초 걸릴 수 있습니다.
                        </div>
                    </div>
                </div>
            ) : null}

            <WeeklyNarrativeEditModal
                open={narrativeOpen}
                onClose={() => {
                    if (isBusy) return;
                    setNarrativeOpen(false);
                    setNarrativeDraft(null);
                    setNarrativeOriginal(null);
                    setNarrativeError(null);
                }}
                loading={narrativeLoading}
                narrative={narrativeDraft}
                onChange={(next) => setNarrativeDraft(next)}
                onRegenerate={onRegenerateNarrative}
                onGeneratePdf={onConfirmPdf}
                busy={isBusy}
                errorMessage={narrativeError}
            />
        </>
    );
}
